import React, { useEffect, useRef, useState } from "react";
import { api } from "../utils/api";
import { PageHeader, Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { TableCard, td, trStyle } from "../components/ui/Table";
import { LoadingRow, EmptyState } from "../components/ui/EmptyState";
import { colors, radius } from "../components/ui/tokens";
import { SearchIcon } from "../components/ui/icons";

// Réservé au compte master (voir App.tsx, route montée seulement si user.is_master_admin) —
// lui seul peut attribuer/changer/retirer un rôle staff (voir OLTODO-backend/src/routes/adminStaff.ts).
// Le compte master lui-même n'apparaît jamais ici : ce n'est pas un "rôle" attribuable.
// Deux rôles seulement : le modérateur voit et modère le contenu (publications, lieux, flash,
// vibes/lives, signalements) — suspend/supprime au besoin ; l'admin a accès à tout le reste
// (utilisateurs, paiements, emails, catalogue...), jamais aux fonctionnalités master (messages
// déchiffrés, connexion "en tant que").
const ROLES = [
  { value: "moderator", label: "Modérateur", sub: "Publications, lieux, flash, vibes & lives, signalements, voir, suspendre, supprimer" },
  { value: "admin", label: "Admin", sub: "Accès opérationnel complet (hors messages et connexion \"en tant que\", réservés au master)" },
];

export default function Staff() {
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadStaff = () => {
    api.get("/admin/staff").then(data => { if (Array.isArray(data?.staff)) setStaff(data.staff); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadStaff(); }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      api.get(`/admin/messages/users/search?q=${encodeURIComponent(query)}`)
        .then(data => setResults(data?.users || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const assignRole = (userId: string, role: string | null) => {
    api.put(`/admin/staff/${userId}/role`, { role }).then(data => {
      if (data?.error) { alert(data.error); return; }
      setQuery(""); setResults([]);
      loadStaff();
    }).catch(() => {});
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Équipe" subtitle="Rôles de modération, réservé au compte principal" />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: colors.text }}>Promouvoir un utilisateur</div>
        <div style={{ position: "relative", maxWidth: 400 }}>
          <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}><SearchIcon size={15} color={colors.textFaint} /></div>
          <input
            value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Rechercher par nom ou email..."
            style={{ width: "100%", padding: "10px 14px 10px 36px", borderRadius: radius.sm, border: `1px solid ${colors.borderStrong}`, fontSize: 14, outline: "none", boxSizing: "border-box" }}
          />
        </div>
        {searching && <div style={{ fontSize: 12, color: colors.textFaint, marginTop: 8 }}>Recherche…</div>}
        {!searching && results.length > 0 && (
          <div style={{ marginTop: 10, border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: "hidden", maxWidth: 460 }}>
            {results.map(u => (
              <div key={u.id} style={{ padding: "10px 14px", borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 8, color: colors.text }}>{u.name} <span style={{ color: colors.textMuted, fontWeight: 400 }}>· {u.email}</span></div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {ROLES.map(r => (
                    <Button key={r.value} small variant="secondary" onClick={() => assignRole(u.id, r.value)}>{r.label}</Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <TableCard headers={["Compte", "Rôle", "Changer de rôle", "Actions"]}>
        {loading ? (
          <tr><td colSpan={4}><LoadingRow /></td></tr>
        ) : staff.length === 0 ? (
          <tr><td colSpan={4}><EmptyState title="Aucun membre de l'équipe pour l'instant" /></td></tr>
        ) : staff.map(s => (
          <tr key={s.id} style={trStyle}>
            <td style={td}>
              <div style={{ fontWeight: 600 }}>{s.name}</div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>{s.email}</div>
            </td>
            <td style={td}>
              <Badge tone={s.staff_role === "admin" ? "accent" : "neutral"}>{ROLES.find(r => r.value === s.staff_role)?.label || s.staff_role}</Badge>
            </td>
            <td style={td}>
              <div style={{ display: "flex", gap: 6 }}>
                {ROLES.filter(r => r.value !== s.staff_role).map(r => (
                  <Button key={r.value} small variant="ghost" onClick={() => assignRole(s.id, r.value)}>→ {r.label}</Button>
                ))}
              </div>
            </td>
            <td style={td}>
              <Button small variant="danger" onClick={() => assignRole(s.id, null)}>Retirer le rôle</Button>
            </td>
          </tr>
        ))}
      </TableCard>
    </div>
  );
}
